import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Modal, Radio, Select, Spin, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import { SalesOrderLine } from '@totem/components/salesOrders/list/types';
import { Ticket } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ForeignKey } from '@totem/types/common';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';
import { debounce } from '@totem/utilities/debounce';
import {
  SALES_ORDERS_ENDPOINT,
  TICKET_ENDPOINT,
} from '@totem/utilities/endpoints';

const DEBOUNCE_TIME = 500;
const LINE_STATUSES = ['Active', 'Pending'];

export type SalesOrderAssociationMode = 'lines' | 'order';

type Props = {
  ticket: Ticket;
  keys: ForeignKey[];
  onClose: () => void;
  onSaved: (keys: ForeignKey[]) => void;
};

const isSet = (id: string) => isNotNull(id) && id !== '' && id !== EMPTY_ID;

export const lineKeyValue = (line: SalesOrderLine) =>
  `${line.nsSalesOrder?.tranId ?? ''}.${line.nsSalesOrderLine?.line ?? ''}`;

const SalesOrderSelectDialog = ({ ticket, keys, onClose, onSaved }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [searching, setSearching] = useState<boolean>(false);
  const [orderOptions, setOrderOptions] = useState<string[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<string>(null);
  const [loadingLines, setLoadingLines] = useState<boolean>(false);
  const [lines, setLines] = useState<SalesOrderLine[]>([]);
  const [mode, setMode] = useState<SalesOrderAssociationMode>('lines');
  const [selectedLineIds, setSelectedLineIds] = useState<React.Key[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const buildScopeFilters = useCallback(() => {
    const filters: { field: string; operator?: string; values: string[] }[] = [
      { field: 'status', values: LINE_STATUSES },
      { field: 'organization.id', values: [ticket.organizationId] },
    ];
    if (isSet(ticket.buildingId)) {
      filters.push({ field: 'building.id', values: [ticket.buildingId] });
    }
    return filters;
  }, [ticket.organizationId, ticket.buildingId]);

  const fetchLines = useCallback(
    (
      extraFilters: { field: string; operator?: string; values: string[] }[],
      pageSize: number,
    ) =>
      fetch(SALES_ORDERS_ENDPOINT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getApiToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paging: { page: 1, pageSize },
          sort: { field: 'nsSalesOrder.tranId', direction: 1 },
          filters: [...buildScopeFilters(), ...extraFilters],
        }),
      })
        .then((res) => res.json())
        .then((result: { lines: SalesOrderLine[] }) => result.lines ?? []),
    [getApiToken, buildScopeFilters],
  );

  const runSearch = useCallback(
    (term: string) => {
      setSearching(true);

      const filters = term
        ? [
            {
              field: 'nsSalesOrder.tranId',
              operator: 'cscontains',
              values: [term],
            },
          ]
        : [];

      fetchLines(filters, 200)
        .then((found) =>
          setOrderOptions(
            Array.from(
              new Set(found.map((line) => line.nsSalesOrder?.tranId)),
            ).filter((tranId) => isNotNull(tranId) && tranId !== ''),
          ),
        )
        .finally(() => setSearching(false));
    },
    [fetchLines],
  );

  const handleSearch = useCallback(debounce(runSearch, DEBOUNCE_TIME), [
    runSearch,
  ]);

  // Pre-populate with the sales orders available to this ticket's location
  useEffect(() => {
    runSearch('');
  }, []);

  useEffect(() => {
    setLines([]);

    setSelectedLineIds([]);

    setError('');

    if (!selectedOrder) {
      return;
    }

    setLoadingLines(true);

    fetchLines([{ field: 'nsSalesOrder.tranId', values: [selectedOrder] }], 500)
      .then((found) => setLines(found))
      .finally(() => setLoadingLines(false));
  }, [selectedOrder]);

  const isLineAssociated = (line: SalesOrderLine) =>
    keys.some(
      (key) =>
        key.system === 'Contracts' &&
        key.instance === 'NetSuite' &&
        key.key === lineKeyValue(line),
    );

  const isOrderAssociated =
    isNotNull(selectedOrder) &&
    keys.some(
      (key) =>
        key.system === 'Contracts' &&
        key.instance === 'NETSUITE_SALES_ORDER_NUMBER' &&
        key.key === selectedOrder,
    );

  const canSave =
    isNotNull(selectedOrder) &&
    lines.length > 0 &&
    (mode === 'order' ? !isOrderAssociated : selectedLineIds.length > 0);

  const handleSave = () => {
    if (!canSave) {
      return;
    }

    setSaving(true);

    setError('');

    fetch(`${TICKET_ENDPOINT}/${ticket.id}/keys/salesOrder`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        salesOrderNumber: selectedOrder,
        lineIds: mode === 'lines' ? selectedLineIds : [],
      }),
    })
      .then((res) => {
        if (res.status !== 200) {
          throw new Error(`Association failed (${res.status})`);
        }
        return res.json();
      })
      .then((updatedKeys: ForeignKey[]) => onSaved(updatedKeys ?? []))
      .catch((err: Error) => setError(err.message))
      .finally(() => setSaving(false));
  };

  const columns: ColumnProps<SalesOrderLine>[] = [
    {
      title: 'Line',
      key: 'line',
      render: (_, line) => lineKeyValue(line),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, line) =>
        line.nsSalesOrderLine?.description || line.nsSalesOrderLine?.itemName,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'Active' ? 'green' : 'gold'}>{status}</Tag>
      ),
    },
    {
      title: 'Services',
      key: 'services',
      render: (_, line) =>
        (line.services ?? []).map((svc) => <Tag key={svc.id}>{svc.name}</Tag>),
    },
    {
      title: 'Start',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (value: string) => formatDateOnly(value),
    },
    {
      title: 'End',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (value: string) => formatDateOnly(value),
    },
    {
      title: 'Building',
      key: 'building',
      render: (_, line) => line.building?.name,
    },
  ];

  return (
    <Modal
      open
      width={1000}
      title={<ModalTitle>Associate Sales Order</ModalTitle>}
      onCancel={onClose}
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!canSave}
          >
            Associate
          </Button>
        </ModalFooter>
      }
    >
      <Select
        showSearch
        allowClear
        style={{ width: '100%' }}
        placeholder="Search Active / Pending sales orders by order number..."
        filterOption={false}
        notFoundContent={searching ? <Spin size="small" /> : 'No results'}
        onSearch={handleSearch}
        onChange={(value: string) => setSelectedOrder(value ?? null)}
        value={selectedOrder}
        options={orderOptions.map((tranId) => ({
          value: tranId,
          label: tranId,
        }))}
      />
      {isNotNull(selectedOrder) && (
        <>
          <Radio.Group
            style={{ marginTop: 12, marginBottom: 12 }}
            value={mode}
            onChange={(evt) => setMode(evt.target.value)}
          >
            <Radio value="lines">Specific line(s)</Radio>
            <Radio value="order">Entire sales order</Radio>
          </Radio.Group>
          <IBTable
            size="small"
            columns={columns}
            dataSource={lines}
            loading={loadingLines}
            rowKey={(line: SalesOrderLine) => line.id}
            rowSelection={
              mode === 'lines'
                ? {
                    selectedRowKeys: selectedLineIds,
                    onChange: (keysSelected: React.Key[]) =>
                      setSelectedLineIds(keysSelected),
                    getCheckboxProps: (line: SalesOrderLine) => ({
                      disabled: isLineAssociated(line),
                    }),
                  }
                : undefined
            }
            pagination={{ showSizeChanger: true, defaultPageSize: 10 }}
          />
          {mode === 'order' && isOrderAssociated && (
            <Alert
              type="info"
              showIcon
              message={`${selectedOrder} is already associated with this ticket.`}
            />
          )}
        </>
      )}
      {error !== '' && (
        <Alert
          style={{ marginTop: 12 }}
          type="error"
          showIcon
          message={error}
        />
      )}
    </Modal>
  );
};

export default SalesOrderSelectDialog;
